<template>
  <div class="c-input-date__wrap">
    <DatePicker
      :value="initValue"
      :min-date="minDate"
      :max-date="maxDate"
      :masks="masksOptions"
      :theme="themesOptions"
      :popover="popoverOptions"
      :is-inline="false"
      :input-props="{
        id: name,
        name: name,
        class: inputClass,
        autocomplete: false,
        placeholder: isIe ? '' : placeholder,
        readonly: false,
        autocomplete: 'off'
      }"
      @input="updateValue($event)"
    >
    </DatePicker>
    <div class="c-input-date__icon">
      <IconBase view-box="0 0 16 16" width="16" height="16">
        <IconCalendar />
      </IconBase>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapState } from "vuex";
import DatePicker from 'v-calendar';
import IconBase from "@/common/components/atoms/IconBase.vue";
import IconCalendar from "@/common/components/icons/IconCalendar.vue";

import commons from "@/common/store/modules/commons";
import globals from "@/general/store/modules/globals";

@Options({
  props: {
    name: { type: String, required: true },
    error: { type: String, required: false },
    placeholder: { type: String, required: false },
    minDate: { type: String, required: false },
    maxDate: { type: String, required: false },
    initValue: { type: [Date, Boolean], required: false },
    isRight: { type: Boolean, required: false }
  },
  components: {
    IconBase,
    IconCalendar,
    DatePicker
  }
})
export default class InputDate extends Vue {
  name!: string;
  error!: string;
  placeholder!: string;
  minDate!: string;
  maxDate!: string;
  initValue!: any;
  isRight!: boolean;

  dateNew: any;
  masksOptions = {
    L: "YYYY/MM/DD",
    title: "YYYY年 MMM"
  };
  themesOptions = {
    container: "p-form-datepicker",
    title: "p-form-datepicker__title",
    arrows: "p-form-datepicker__arrows",
    bgAccentHigh: "p-form-datepicker__selected",
    weekdays: "p-form-datepicker__weekdays",
    dayContent: "p-form-datepicker__day",
    dayContentDisabled: "p-form-datepicker__day--disabled"
  };

  updateValue(value: string) {
    if (value) this.$emit("input", { value, name: this.name });
  }

  get color(): string {
    return commons.color;
  }

  get browser() {
    return globals.browser;
  }

  get isSp() {
    return globals.isLargeBreakpointWidthOfSp;
  }

  get datePickerTheme() {
    return `p-form-datepicker p-form-datepicker--${this.color}`;
  }

  get inputClass(): string {
    let inputClass = "c-input-date c-input-date--has_icon";
    if (this.error) inputClass += " c-input-date--error";
    return inputClass;
  }

  get popoverOptions() {
    return {
      visibility: "focus",
      placement: this.isSp && this.isRight ? "bottom-end" : "bottom-start",
      positionFixed: true
    };
  }

  get isIe() {
    return globals.browser === "ie";
  }

  mounted() {
    this.dateNew = this.initValue;
    this.themesOptions.bgAccentHigh = `p-form-datepicker__selected p-form-datepicker__selected--${this.color}`;
  }
}
</script>

<style lang="scss">
.c-input-date {
  font-size: rem(14);
  line-height: normal;
  width: 100%;
  border: px(1) solid $common-gray-border;
  border-radius: px(4);
  padding: rem(10) rem(48) rem(11) rem(12);
  display: block;

  @include sp {
    padding: rem(10) rem(32) rem(11) rem(12);
  }

  &__wrap {
    position: relative;
    width: rem(160);
  }

  &__icon {
    color: $common-gray500;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: px(48);
    height: 100%;
    margin: auto;
    display: flex;
    align-items: center;
    justify-content: center;

    @include sp {
      width: px(32);
    }
  }

  &--error {
    border-color: $error;
  }

  &::placeholder {
    color: $common-gray300;
  }

  &:focus {
    border-color: $common-black;
  }

  & + .vc-popover-content-wrapper {
    z-index: 9999 !important;
  }
}
.p-form-datepicker {
  box-shadow: 0 rem(3) rem(7) rgba(0, 0, 0, 0.1);
  background: #fafafa;
  border: px(1) solid #bdbdbd;
  border-radius: px(4);
  z-index: -1;

  .vc-grid-container {
    display: grid;
    &.vc-weeks {
      padding: rem(5) rem(6) rem(7) rem(6);
    }
    .vc-day-content {
      min-height: rem(32);
      width: rem(32);
      height: rem(32);
      margin: rem(2) auto;
    }
    .vc-header {
      padding: rem(10) rem(10) 0 rem(10);
    }
  }

  & + .vc-popover-caret.align-left {
    left: rem(16);
  }

  &__title {
    color: $common-gray900;
    font-size: rem(14);
    font-weight: bold;
  }

  &__arrows {
    .vc-svg-icon {
      transform: scale(0.75);
    }
  }

  &__weekdays {
    color: $common-gray700;
    font-size: rem(12);
  }

  &__day {
    font-size: rem(12);
    cursor: pointer;

    &--disabled {
      color: $common-gray300;
      cursor: default;
    }
  }

  &__selected {
    &--primary {
      background: $common-primary;
    }

    &--blue {
      background: $customize-blue;
    }

    &--green {
      background: $customize-green;
    }

    &--yellow {
      background: $customize-yellow;
    }

    &--pink {
      background: $customize-pink;
    }
  }
}
</style>
